/**
 * Why Google sign-in did not happen, in words that name the actual cause.
 *
 * ROW 7fe3d3db. Google Identity Services reports a failed prompt through a
 * notification object that carries a REASON — `getNotDisplayedReason()` or
 * `getSkippedReason()`. Both were already declared in social-auth.d.ts and
 * neither was ever read. Login.tsx collapsed every outcome into one sentence:
 *
 *     "Google sign-in was cancelled. Please try again or use email/password."
 *
 * So `unregistered_origin` — this site is not on the OAuth client's authorised
 * JavaScript origins, which no customer can do anything about and which is the
 * single most likely way Google sign-in fails in production — was reported to
 * the user as though they had cancelled it. So were `invalid_client`,
 * `missing_client_id` and `secure_http_required`. A configuration failure that
 * presents as a user action is invisible: nobody files it, and the fix is
 * never made because nobody knows there is one.
 *
 * THE RULE THIS FILE KEEPS: the raw reason code is ALWAYS carried through, even
 * when the mapping does not recognise it. Google adds reasons; a message that
 * dropped an unfamiliar code would put us straight back where we started, and
 * the whole point is that the datum reaches a human.
 */

/** What the person reading the message can do about it. */
export type GoogleSignInBlame =
  /** Nothing. FTL or the Google project is misconfigured. */
  | "configuration"
  /** Something about this browser or session — the user can act. */
  | "user"
  /** Google itself declined to issue a credential. */
  | "google"
  /** The person genuinely dismissed the prompt. */
  | "cancelled";

export interface GoogleSignInFailure {
  blame: GoogleSignInBlame;
  reason: string;
  message: string;
}

/**
 * Reasons that mean the Google project or this deployment is wrong.
 * None of them is the user's doing and none improves by trying again.
 */
const CONFIGURATION: Record<string, string> = {
  unregistered_origin:
    "Google sign-in is not enabled for this site. The address you are on is " +
    "not one of the authorised origins on our Google project.",
  invalid_client:
    "Google did not recognise our sign-in application.",
  missing_client_id:
    "Google sign-in is not configured here — no client id reached the page.",
  secure_http_required:
    "Google sign-in requires a secure (https) connection and this page is not " +
    "being served over one.",
};

/** Reasons the person in front of the screen can actually resolve. */
const USER: Record<string, string> = {
  opt_out_or_no_session:
    "You are not signed in to Google in this browser, or you have turned off " +
    "Google sign-in prompts for this site.",
  suppressed_by_user:
    "Google has stopped showing this prompt because it was dismissed several " +
    "times. Signing in at google.com first will bring it back.",
  browser_not_supported:
    "This browser does not support Google sign-in.",
};

/** The prompt was skipped. Only some of these are a real cancellation. */
const SKIPPED: Record<string, { blame: GoogleSignInBlame; message: string }> = {
  user_cancel: { blame: "cancelled", message: "Google sign-in was cancelled." },
  tap_outside: { blame: "cancelled", message: "Google sign-in was dismissed." },
  auto_cancel: { blame: "cancelled", message: "Google sign-in closed itself." },
  issuing_failed: {
    blame: "google",
    message: "Google could not issue a sign-in credential.",
  },
};

const FALLBACK = "Please try again, or use email and password.";
const CONTACT = "This is not something you can fix — please tell your administrator.";

/**
 * Turn a GIS prompt notification into a failure a human can act on.
 *
 * Returns null when the notification is not a failure at all, so a caller can
 * pass every moment through without deciding first.
 *
 * `notification` is intentionally typed loosely: this must survive Google
 * adding a method, and a missing getter must not throw inside a sign-in
 * handler — a crash here would read as "sign-in is broken" rather than as
 * "we could not tell you why".
 */
export function googleSignInFailure(
  notification: {
    isNotDisplayed?: () => boolean;
    isSkippedMoment?: () => boolean;
    getNotDisplayedReason?: () => string;
    getSkippedReason?: () => string;
  },
): GoogleSignInFailure | null {
  const notDisplayed = safeBool(notification.isNotDisplayed);
  const skipped = safeBool(notification.isSkippedMoment);
  if (!notDisplayed && !skipped) return null;

  if (notDisplayed) {
    const reason = safeString(notification.getNotDisplayedReason);
    if (CONFIGURATION[reason]) {
      return {
        blame: "configuration",
        reason,
        message: `${CONFIGURATION[reason]} ${CONTACT} (${reason})`,
      };
    }
    if (USER[reason]) {
      return { blame: "user", reason, message: `${USER[reason]} (${reason})` };
    }
    // UNKNOWN, AND THE CODE STILL TRAVELS. Google adds reasons; swallowing an
    // unfamiliar one is the defect this file exists to remove.
    return {
      blame: "configuration",
      reason: reason || "unknown_reason",
      message:
        `Google did not show the sign-in prompt. ${FALLBACK} ` +
        `(${reason || "unknown_reason"})`,
    };
  }

  const reason = safeString(notification.getSkippedReason);
  const known = SKIPPED[reason];
  if (known) {
    return {
      blame: known.blame,
      reason,
      message: `${known.message} ${FALLBACK} (${reason})`,
    };
  }
  return {
    blame: "google",
    reason: reason || "unknown_reason",
    message: `Google sign-in did not complete. ${FALLBACK} (${reason || "unknown_reason"})`,
  };
}

function safeBool(fn?: () => boolean): boolean {
  try {
    return typeof fn === "function" ? Boolean(fn()) : false;
  } catch {
    return false;
  }
}

function safeString(fn?: () => string): string {
  try {
    return typeof fn === "function" ? String(fn() ?? "") : "";
  } catch {
    return "";
  }
}
